import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import i18n from '../i18n';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency (EUR) - Dynamic locale-aware formatting
export function formatCurrency(amount, customLocale) {
  if (amount === null || amount === undefined || isNaN(amount)) return '-';
  const locale = customLocale || (i18n.language && i18n.language.startsWith('en') ? 'en-US' : 'de-DE');
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

// Format date - Dynamic locale-aware formatting
export function formatDate(dateString, customLocale) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const resolvedLocale =
    typeof customLocale === 'string'
      ? customLocale
      : (i18n.language && i18n.language.startsWith('en') ? 'en-GB' : 'de-DE');
  return new Intl.DateTimeFormat(resolvedLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

// Format date with a long month name (e.g. 12. März 2026).
export function formatLongDate(dateString, customLocale) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const resolvedLocale =
    typeof customLocale === 'string'
      ? customLocale
      : (i18n.language && i18n.language.startsWith('en') ? 'en-GB' : 'de-DE');
  return new Intl.DateTimeFormat(resolvedLocale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

// Format date and time for timeline/list displays.
export function formatDateTime(dateString, customLocale) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const resolvedLocale =
    typeof customLocale === 'string'
      ? customLocale
      : (i18n.language && i18n.language.startsWith('en') ? 'en-GB' : 'de-DE');
  return new Intl.DateTimeFormat(resolvedLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// Status badge styles
export const statusStyles = {
  // Owner/Tenant status
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  inactive: 'bg-slate-100 text-slate-800 dark:bg-muted dark:text-muted-foreground',
  prospect: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
  former: 'bg-slate-100 text-slate-800 dark:bg-muted dark:text-muted-foreground',

  // Apartment status
  vacant: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
  rented: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  renovation: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  notice_period: 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300',
  reserved: 'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300',

  // Payment status
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  partial: 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300',
  overpaid: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
  late: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  missing: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  waived: 'bg-slate-100 text-slate-800 dark:bg-muted dark:text-muted-foreground',

  // Contract status
  ended: 'bg-slate-100 text-slate-800 dark:bg-muted dark:text-muted-foreground',
  terminated: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  draft: 'bg-slate-100 text-slate-800 dark:bg-muted dark:text-muted-foreground',

  // Reminder status
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
  resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  escalated: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  cancelled: 'bg-slate-100 text-slate-800 dark:bg-muted dark:text-muted-foreground',

  // Task status
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
  in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
};

// Format date for input fields with invalid date protection
export function formatDateForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  try {
    return date.toISOString().split('T')[0];
  } catch (e) {
    return '';
  }
}

// Status labels — i18n-aware via Proxy
export const statusLabels = new Proxy({}, {
  get: (_, key) => i18n.t(`status.${key}`, { defaultValue: key })
});

// Owner type labels
export const ownerTypeLabels = new Proxy({}, {
  get: (_, key) => i18n.t(`ownerTypes.${key}`, { defaultValue: key })
});

// Contract type labels — i18n-aware via Proxy
export const contractTypeLabels = new Proxy({}, {
  get: (_, key) => i18n.t(`contractTypes.${key}`, { defaultValue: key })
});

// Payment category labels — i18n-aware via Proxy
export const paymentCategoryLabels = new Proxy({}, {
  get: (_, key) => i18n.t(`payments.categories.${key}`, { defaultValue: key })
});

// Berlin Bezirke
export const bezirke = [
  'Mitte',
  'Friedrichshain-Kreuzberg',
  'Pankow',
  'Charlottenburg-Wilmersdorf',
  'Spandau',
  'Steglitz-Zehlendorf',
  'Tempelhof-Schöneberg',
  'Neukölln',
  'Treptow-Köpenick',
  'Marzahn-Hellersdorf',
  'Lichtenberg',
  'Reinickendorf',
];

// Heating types
export const heatingTypes = {
  gas: 'Gas',
  oil: 'Öl',
  district: 'Fernwärme',
  electric: 'Elektro',
  heat_pump: 'Wärmepumpe',
  other: 'Sonstige',
};
